/**
 * Least-recently-used cache bounded by entry count, byte budget, or both.
 *
 * Sizes come from the caller when it already knows them (a fetch knows how many
 * bytes it received); otherwise `sizeOf` estimates by serialising, which is
 * only worth doing for small values.
 *
 * Every admission and eviction is logged, because what a cache dropped and how
 * much room is left are not recoverable after the fact. Evictions log at `info`
 * and admissions at `debug`: the interesting event in a report from the field
 * is almost always something going missing. Only keys and sizes are logged,
 * never a cached value.
 */

import { formatBytes } from "./format-bytes";

const BYTES_PER_KB = 1024;

function estimateSize(data) {
  try {
    return new Blob([typeof data === "string" ? data : JSON.stringify(data)]).size;
  } catch {
    return 0;
  }
}

export class LRUCache {
  /**
   * @param {object} options
   * @param {number} [options.maxEntries] Hard cap on entries, regardless of size.
   * @param {number} [options.maxBytes] Byte budget across all entries.
   * @param {(data: unknown) => number} [options.sizeOf] Size estimator for `set` calls without an explicit size.
   * @param {boolean} [options.admitOversized] Store a value larger than the whole
   *   budget anyway, after dropping everything else, instead of refusing it.
   * @param {string} [options.label] Name of what is cached, used to tag its logs.
   */
  constructor({
    maxEntries = Infinity,
    maxBytes = Infinity,
    sizeOf = estimateSize,
    admitOversized = false,
    label = "value",
  } = {}) {
    this.maxEntries = maxEntries;
    this.maxBytes = maxBytes;
    this.sizeOf = sizeOf;
    this.admitOversized = admitOversized;
    this.label = label;
    this.cache = new Map();
    this.bytes = 0;
  }

  get(key) {
    if (!this.cache.has(key)) return undefined;

    const entry = this.cache.get(key);
    this.cache.delete(key);
    this.cache.set(key, entry);
    return entry.data;
  }

  set(key, data, size = this.sizeOf(data)) {
    // A size that is not a byte count would be added to `bytes` and poison it:
    // once the total is NaN every budget comparison is false, so nothing is
    // ever evicted again and the cache grows without limit.
    if (!Number.isFinite(size) || size < 0) {
      console.warn(`[${this.label}] refused ${key}, ${size} is not a byte count`);
      return false;
    }

    // Checked before evicting anything, so a rejected value does not cost the
    // caller whatever was cached under the same key.
    if (size > this.maxBytes && !this.admitOversized) {
      console.warn(
        `[${this.label}] refused ${key}, ${formatBytes(size)} exceeds the ${this.budget} budget`,
      );
      return false;
    }

    this.delete(key);

    // Nothing else can coexist with an oversized value, so start from an empty
    // cache rather than evicting one entry at a time down to the same place.
    if (size > this.maxBytes) {
      console.warn(
        `[${this.label}] ${formatBytes(size)} exceeds the ${this.budget} budget, dropping all ${this.cache.size} entries for it`,
      );
      this.clear();
    }

    while (
      this.cache.size > 0 &&
      (this.cache.size >= this.maxEntries || this.bytes + size > this.maxBytes)
    ) {
      const oldest = this.cache.keys().next().value;

      console.info(
        `[${this.label}] evicted ${oldest}, ${formatBytes(this.cache.get(oldest).size)}, to make room for ${formatBytes(size)}`,
      );

      this.delete(oldest);
    }

    this.cache.set(key, { data, size });
    this.bytes += size;

    console.debug(
      `[${this.label}] cached ${key}, ${formatBytes(size)}, now ${formatBytes(this.bytes)} of ${this.budget} across ${this.cache.size} entries`,
    );

    return true;
  }

  has(key) {
    return this.cache.has(key);
  }

  delete(key) {
    const entry = this.cache.get(key);
    if (!entry) return false;

    this.bytes -= entry.size;
    this.cache.delete(key);
    return true;
  }

  clear() {
    this.cache.clear();
    this.bytes = 0;
  }

  get size() {
    return this.cache.size;
  }

  get budget() {
    return Number.isFinite(this.maxBytes) ? formatBytes(this.maxBytes) : "unbounded";
  }
}

export const megabytes = (mb) => mb * BYTES_PER_KB * BYTES_PER_KB;
